import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable, { Styles } from 'jspdf-autotable';
import { Borders, Cell, Workbook, Worksheet } from 'exceljs';
import { DLEReportData } from '../../models/lab-test';
import { DifferentialLiberation, DLEResidualOilCompositions, DLEVaporStageCompositions } from '../../dle/models/dle';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { ReportPDFTableHelpersService } from './report.pdfTableHelpers.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';

@Injectable({
    providedIn: 'root'
})
export class ReportPDFDiffLibRenderService {
    private reportPDFTableHelpersService = inject(ReportPDFTableHelpersService);
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);

    private commonStyles = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: reportConstants.tableLinewidth
    };

    private commonHeadStyles = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesOthers = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 }
    };

    private commonStylesWithoutBorder = {
        fontSize: 8,
        textColor: [0, 0, 0],
        fillColor: false,
        lineColor: [0, 0, 0],
        lineWidth: 0
    };

    private commonHeadStylesWithoutBorder = {
        fontSize: 10,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        fontStyle: reportConstants.normal,
        font: reportConstants.eMprintCondensedBold,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    private commonBodyStylesWithoutBorder = {
        fontSize: 8,
        halign: reportConstants.center,
        textColor: [0, 0, 0],
        fillColor: false,
        font: reportConstants.eMprintCondensedRegular,
        cellPadding: { top: 0.5, bottom: 0.5 },
        lineWidth: 0
    };

    DiffLibTest(doc: jsPDF, marginSize: number, dleExp: DLEReportData, appendix = '', wellNameVersion: string, sampleNameVersion: string): void {
        if (dleExp !== undefined && dleExp !== null) {
            doc.addPage();
            let tableStartY = marginSize;
            tableStartY += 20;
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(appendix + reportConstants.differentialLiberation, marginSize - 7, tableStartY);
            tableStartY += 5;
            if (appendix !== '') {
                doc.setFontSize(10);
                doc.setFont(reportConstants.eMprintCondensedRegular);
                doc.text(wellNameVersion + ' ' + sampleNameVersion, marginSize - 7, tableStartY);
            }
            tableStartY += 5;
            autoTable(doc, {
                head: reportConstants.headDL,
                body: dleExp.dleData.diffLibResult?.diffLibResult.map((val: DifferentialLiberation) =>
                    [
                        val.stage ?? reportConstants.dash,
                        val.temperature ?? reportConstants.dash,
                        this.formatDifferentialLiberationValue(val.pressurePsia, 0),
                        this.formatDifferentialLiberationValue(val.stageGORScfStb, 2),
                        this.formatDifferentialLiberationValue(val.solutionGORScfStb, 1),
                        this.formatDifferentialLiberationValue(val.cumulativeGORScfStb, 1),
                        this.formatDifferentialLiberationValue(val.cumulativeGORSatPScfBbl, 1),
                        this.formatDifferentialLiberationValue(val.oilFVFBblStb, 4),
                        this.formatDifferentialLiberationValue(val.shrinkageFactor, 4),
                        this.formatDifferentialLiberationValue(val.oilDensity, 4),
                        this.formatDifferentialLiberationValue(val.gasDeviationFactor, 4),
                        this.formatDifferentialLiberationValue(val.gasFVF, 4),
                        this.formatDifferentialLiberationValue(val.gasGravity, 4)
                    ]) ?? [],
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize - 7, right: marginSize - 7 },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: {
                    fontSize: 10,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    fontStyle: reportConstants.normal,
                    font: reportConstants.eMprintCondensedBold,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: 0
                },
                bodyStyles: this.commonBodyStylesOthers as Partial<Styles>,
                didDrawCell: (data) => {
                    const { cell, row, doc, section } = data;
                    if (section === reportConstants.head) {
                        if (row.index === 0) {
                            this.reportPDFTableHelpersService.drawTopLeftRightBorders(doc, cell, reportConstants.tableLinewidth);
                        } else if (row.index === 3) {
                            this.reportPDFTableHelpersService.drawDoubleBottomLeftRightBorders(doc, cell, reportConstants.tableLinewidth);
                        } else {
                            this.reportPDFTableHelpersService.drawLeftRightBorders(doc, cell, reportConstants.tableLinewidth);
                        }
                    } else {
                        this.reportPDFTableHelpersService.drawFullCellBorder(doc, cell, reportConstants.tableLinewidth);
                    }
                }
            });
            tableStartY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 7;
            doc.setFontSize(12);
            doc.setFont(reportConstants.eMprintCondensedRegular);
            const residualOilGravityVal = reportConstants.residualOilGravity + this.formatDifferentialLiberationValue(dleExp.dleData.diffLibResult?.residualOilGravityAPI, 1);
            doc.text(residualOilGravityVal, marginSize + 20, tableStartY);
            doc.setFontSize(10);
            tableStartY += 10;
            doc.setFont(reportConstants.eMprintCondensedRegular);
            this.reportPDFTableHelpersService.writeStackedComments(
                doc,
                marginSize,
                tableStartY,
                [reportConstants.comments, ...reportConstants.differentialLiberationComments]
            );
        }
    }

    DiffLibTestAppendix(doc: jsPDF, marginSize: number, dleExp: DLEReportData): void {
        if (dleExp !== undefined && dleExp !== null) {
            marginSize += 25;
            doc.addPage();
            let tableStartY = marginSize - 15;
            doc.setFontSize(14);
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.differentialLiberationVaporCompositions, marginSize, tableStartY);
            tableStartY += 5;

            const headerDataVapor = dleExp.dleData.vaporStageCompositionResult?.vaporPressure ?? [];
            const gridData = dleExp.dleData.vaporStageCompositionResult?.gridListData ?? [];

            const stageCount = dleExp.dleData.diffLibResult.diffLibResult.length - 2;
            const stageKeys = Array.from({ length: stageCount }, (_, i) => `stage${i + 1}`);

            const headRows: (string | number)[][] = [
                ['', ...stageKeys.map((_, i) => `Stage ${i + 1}`)],
                [reportConstants.pressurePsia, ...stageKeys.map(key => headerDataVapor[key as keyof typeof headerDataVapor] ?? '')],
                [reportConstants.component, ...stageKeys.map(() => reportConstants.molePercent)]
            ];

            const bodyRows = gridData.map((val: DLEVaporStageCompositions) => [
                val.pressure ?? '',
                ...stageKeys.map(key => this.formatDleVaporCompositionValue(val[key as keyof DLEVaporStageCompositions], val.pressure))
            ]);

            autoTable(doc, {
                head: headRows,
                body: bodyRows,
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                pageBreak: reportConstants.auto,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: (data) => {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        this.reportPDFTableHelpersService.clearCellBorder(doc, cell, reportConstants.tableLinewidth);
                    } else {
                        this.reportPDFTableHelpersService.drawFullCellBorder(doc, cell, reportConstants.tableLinewidth);
                    }

                    if (section === reportConstants.head && row.index === 2) {
                        this.reportPDFTableHelpersService.drawDoubleBottomLeftRightBorders(doc, cell, reportConstants.tableLinewidth);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.addPage();
            tableStartY = marginSize - 20;
            marginSize += 5;
            doc.setFont(reportConstants.eMprintCondensedBold);
            doc.text(reportConstants.dlResidualOilComposition, marginSize, tableStartY);
            tableStartY += 5;
            autoTable(doc, {
                head: [
                    [
                        { content: '', colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: reportConstants.residualOil, colSpan: 2, styles: { halign: reportConstants.center } }
                    ],
                    [
                        { content: reportConstants.compound, colSpan: 2, styles: { halign: reportConstants.center } },
                        { content: reportConstants.weightPercent, styles: { halign: reportConstants.center } },
                        { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                    ]
                ],
                body: dleExp.dleData.residualOilCompositionResult?.map((val: DLEResidualOilCompositions) => [
                    val.component1,
                    val.component2,
                    this.formatDleResidualOilCompositionValue(val.weightPercent, val.component1),
                    this.formatDleResidualOilCompositionValue(val.molePercent, val.component1)
                ]),
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStylesWithoutBorder as Partial<Styles>,
                headStyles: this.commonHeadStylesWithoutBorder as Partial<Styles>,
                bodyStyles: this.commonBodyStylesWithoutBorder as Partial<Styles>,
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.font = reportConstants.eMprintCondensedBold;
                    }
                },
                didDrawCell: (data) => {
                    const { cell, row, column, doc, section } = data;
                    if (section === reportConstants.head && row.index === 0 && column.index === 0) {
                        this.reportPDFTableHelpersService.clearCellBorder(doc, cell, reportConstants.tableLinewidth);
                    } else {
                        this.reportPDFTableHelpersService.drawFullCellBorder(doc, cell, reportConstants.tableLinewidth);
                    }
                    if (section === reportConstants.head && row.index === 1) {
                        this.reportPDFTableHelpersService.drawDoubleBottomLeftRightBorders(doc, cell, reportConstants.tableLinewidth, 0.5);
                    }
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.addPage();
            marginSize -= 20;
            tableStartY = marginSize;
            doc.text(reportConstants.materialBalanceCalculationSummary, marginSize, tableStartY);
            tableStartY += 10;
            doc.text(reportConstants.massesOfReservoirFluid, marginSize, tableStartY + 3);
            const massesReservoirFluid = dleExp.dleData.materialBalanceSummaryResult?.massesReservoirFluid ?? [];
            const constTableData = [
                [reportConstants.bcaInitalMass, massesReservoirFluid[0].initialMass, reportConstants.g],
                [reportConstants.bcaMaterialBalanceCalculatedMass, massesReservoirFluid[0].mbCalculatedMass, reportConstants.g],
                [reportConstants.dlDifference, massesReservoirFluid[0].diff, reportConstants.percentage]
            ];

            autoTable(doc, {
                head: [['', '', '']],
                body: constTableData,
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });

            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
            doc.text(reportConstants.massesOfStockTankOil, marginSize, tableStartY + 3);
            const massesStockTankOil = dleExp.dleData.materialBalanceSummaryResult?.massesStockTankOil ?? [];
            const dleTableData = [
                [reportConstants.dlMeasuredSTOMass, massesStockTankOil[0].measuredSTOmass, reportConstants.g],
                [reportConstants.dlMaterialBalanceCalculatedSTOMass, massesStockTankOil[0].calculatedSTOmass, reportConstants.g],
                [reportConstants.dlDifference, massesStockTankOil[0].diff, reportConstants.percentage]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: dleTableData,
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });
            tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

            doc.text(reportConstants.dlMolesOfReservoirFluid, marginSize, tableStartY + 3);
            const molesReservoirFluid = dleExp.dleData.materialBalanceSummaryResult?.molesReservoirFluid ?? [];
            const molesTableData = [
                [reportConstants.initialMoles, molesReservoirFluid[0].initialMoles, reportConstants.mol],
                [reportConstants.materialBalanceCalculatedMoles, molesReservoirFluid[0].calculatedMoles, reportConstants.mol],
                [reportConstants.dlDifference, molesReservoirFluid[0].diff, reportConstants.percentage]
            ];
            autoTable(doc, {
                head: [['', '', '']],
                body: molesTableData,
                startY: Math.round(tableStartY),
                theme: reportConstants.plain,
                margin: { left: marginSize, right: marginSize },
                styles: this.commonStyles as Partial<Styles>,
                headStyles: this.commonHeadStyles as Partial<Styles>,
                bodyStyles: {
                    fontSize: 12,
                    halign: reportConstants.center,
                    textColor: [0, 0, 0],
                    fillColor: false,
                    font: reportConstants.eMprintCondensedRegular,
                    cellPadding: { top: 0.5, bottom: 0.5 },
                    lineWidth: reportConstants.tableLinewidth
                },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 20 }
                },
                didParseCell: function (data) {
                    if (data.section === reportConstants.body && data.column.index === 0) {
                        data.cell.styles.halign = reportConstants.left;
                        data.cell.styles.cellPadding = { left: 2, right: 0, top: 0.5, bottom: 0.5 };
                    }
                    if (data.section === reportConstants.body && data.column.index === 2) {
                        data.cell.styles.halign = reportConstants.left;
                    }
                    data.cell.styles.cellPadding = { left: 2, right: 2, top: 0.5, bottom: 0.5 };
                }
            });
        }
    }

    async GenerateDiffLibTest(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string, appendix = '') {
        try {
            const diffLibTestWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle = {
                top: { style: reportConstants.medium },
                left: { style: reportConstants.medium },
                bottom: { style: reportConstants.medium },
                right: { style: reportConstants.medium }
            };

            diffLibTestWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(diffLibTestWorksheet, undefined, {
                orientation: reportConstants.landscape,
                scale: 88
            });
            diffLibTestWorksheet.getColumn(reportConstants.a).width = 4;

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            diffLibTestWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(diffLibTestWorksheet, subHeader, reportConstants.c1, reportConstants.l1);
            diffLibTestWorksheet.getColumn(reportConstants.o).width = 5;
            diffLibTestWorksheet.getRow(3).height = 20;
            const textReportSubtitleCell = diffLibTestWorksheet.getCell(reportConstants.a3);
            textReportSubtitleCell.value = appendix + reportConstants.differentialLiberation;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            let rowNo = 5;
            const sampleHead = [
                reportConstants.DiffLibTest as [string, string, string, string, string, string, string, string, string, string, string, string, string]
            ];

            const sampleBody = dleExp.dleData.diffLibResult?.diffLibResult.map((val: DifferentialLiberation) => [
                val.stage ?? reportConstants.dash,
                val.temperature ?? reportConstants.dash,
                this.formatDifferentialLiberationValue(val.pressurePsia, 0),
                this.formatDifferentialLiberationValue(val.stageGORScfStb, 2),
                this.formatDifferentialLiberationValue(val.solutionGORScfStb, 1),
                this.formatDifferentialLiberationValue(val.cumulativeGORScfStb, 1),
                this.formatDifferentialLiberationValue(val.cumulativeGORSatPScfBbl, 1),
                this.formatDifferentialLiberationValue(val.oilFVFBblStb, 4),
                this.formatDifferentialLiberationValue(val.shrinkageFactor, 4),
                this.formatDifferentialLiberationValue(val.oilDensity, 4),
                this.formatDifferentialLiberationValue(val.gasDeviationFactor, 4),
                this.formatDifferentialLiberationValue(val.gasFVF, 4),
                this.formatDifferentialLiberationValue(val.gasGravity, 4)
            ]) ?? [];

            const tableStartRow = rowNo++;
            this.GenerateTableWithBordersNew(diffLibTestWorksheet, sampleHead, sampleBody, tableStartRow, 2, borderStyle);

            diffLibTestWorksheet.getColumn(2).width = 7;
            diffLibTestWorksheet.getColumn(8).width = 14;
            diffLibTestWorksheet.getColumn(10).width = 12;
            diffLibTestWorksheet.getColumn(12).width = 12;
            rowNo = tableStartRow + sampleHead.length + sampleBody.length;
            rowNo++;

            diffLibTestWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.d + rowNo);
            const textResidualOilGravityCell = diffLibTestWorksheet.getCell(reportConstants.d + rowNo);
            textResidualOilGravityCell.value = reportConstants.residualOilGravity;
            textResidualOilGravityCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textResidualOilGravityCell.alignment = { horizontal: reportConstants.right, vertical: reportConstants.middle };

            const valueResidualOilGravityCell = diffLibTestWorksheet.getCell(reportConstants.e + rowNo);
            setNumericOrTextCellValue(valueResidualOilGravityCell, this.formatDifferentialLiberationValue(dleExp?.dleData?.diffLibResult?.residualOilGravityAPI, 1));
            valueResidualOilGravityCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            valueResidualOilGravityCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;

            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.comments);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[0]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[1]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[2]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[3]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[4]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[5]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[6]);
            rowNo++;
            this.AddComment(diffLibTestWorksheet, reportConstants.b, rowNo, reportConstants.differentialLiberationComments[7]);

            rowNo += 2;
            this.AddOuterBorder(diffLibTestWorksheet, 3, rowNo, 1, 15);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    private formatDifferentialLiberationValue(value: number | string | null | undefined, decimalPlaces: number): string {
        if (value === null || value === undefined) return reportConstants.dash;
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? String(value) : numericValue.toFixed(decimalPlaces);
    }

    private formatDleVaporCompositionValue(value: number | string | null | undefined, rowLabel: string | null | undefined): string {
        if (value === null || value === undefined || value === '') return '';
        const numericValue = Number(value);
        return Number.isNaN(numericValue) ? String(value) : numericValue.toFixed(rowLabel === reportConstants.totals ? 2 : 3);
    }

    private formatDleResidualOilCompositionValue(value: number | null | undefined, rowLabel: string | null | undefined): string {
        return value === null || value === undefined ? '' : value.toFixed(rowLabel === reportConstants.totals ? 2 : 3);
    }

    async GenerateDiffLibTestVaporCompositions(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        try {
            const DiffTestWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            DiffTestWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(DiffTestWorksheet, subHeader, reportConstants.c1, reportConstants.i1);

            DiffTestWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(DiffTestWorksheet, undefined, {
                orientation: reportConstants.portrait,
                fitToHeight: 0,
                fitToWidth: 0,
                fitToPage: false,
                scale: 50,
            });

            const stagesData = dleExp.dleData.diffLibResult?.diffLibResult.filter((s: DifferentialLiberation) => s.stage !== reportConstants.p_Sat) ?? [];
            const stageCount = dleExp.dleData.diffLibResult.diffLibResult.length - 2;
            const vaporData = dleExp.dleData.vaporStageCompositionResult?.gridListData || [];
            if (vaporData.length === 0) return;

            const lastCol = stageCount + 2;

            DiffTestWorksheet.mergeCells(reportConstants.a5, reportConstants.f5);
            DiffTestWorksheet.getRow(5).height = 15;
            const titleCell = DiffTestWorksheet.getCell(reportConstants.a4);
            titleCell.value = reportConstants.differentialLiberationVaporCompositions;
            titleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };

            DiffTestWorksheet.getCell(7, 1).value = '';
            stagesData.forEach((stage: DifferentialLiberation, i: number) => {
                if (i === stagesData.length - 1) return;
                const colIndex = i + 2;
                DiffTestWorksheet.getCell(7, colIndex).value = `Stage ${i + 1}`;
                DiffTestWorksheet.getCell(7, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                DiffTestWorksheet.getCell(7, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });
            DiffTestWorksheet.getCell(8, 1).value = reportConstants.pressurePsia;
            DiffTestWorksheet.getCell(8, 1).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            DiffTestWorksheet.getCell(8, 1).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            stagesData.forEach((stage: DifferentialLiberation, i: number) => {
                if (i === stagesData.length - 1) return;
                const colIndex = i + 2;
                setNumericOrTextCellValue(DiffTestWorksheet.getCell(8, colIndex), stage.pressurePsia ?? '');
                DiffTestWorksheet.getCell(8, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                DiffTestWorksheet.getCell(8, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });
            DiffTestWorksheet.getCell(9, 1).value = reportConstants.component;
            DiffTestWorksheet.getCell(9, 1).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            DiffTestWorksheet.getCell(9, 1).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

            stagesData.forEach((stage: DifferentialLiberation, i: number) => {
                if (i === stagesData.length - 1) return;
                const colIndex = i + 2;
                DiffTestWorksheet.getCell(9, colIndex).value = reportConstants.molePercent;
                DiffTestWorksheet.getCell(9, colIndex).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                DiffTestWorksheet.getCell(9, colIndex).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            });

            const dataRowStart = 10;
            vaporData.forEach((comp: DLEVaporStageCompositions, idx: number) => {
                const rowIndex = dataRowStart + idx;
                setNumericOrTextCellValue(DiffTestWorksheet.getCell(rowIndex, 1), comp.pressure ?? '');
                DiffTestWorksheet.getCell(rowIndex, 1).font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                DiffTestWorksheet.getCell(rowIndex, 1).alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };

                stagesData.forEach((stage: DifferentialLiberation, i: number) => {
                    if (i === stagesData.length - 1) return;
                    const colIndex = i + 2;
                    const value = comp[`stage${i + 1}`];
                    const cell = DiffTestWorksheet.getCell(rowIndex, colIndex);
                    setNumericOrTextCellValue(cell, value === 0 ? '' : this.formatDleVaporCompositionValue(value, comp.pressure));
                    cell.font = { name: reportConstants.eMprintCondensed, size: 11 };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                });
            });

            const lastRow = dataRowStart + vaporData.length - 1;

            DiffTestWorksheet.getRow(4).eachCell((cell: Cell) => {
                cell.border = { left: { style: reportConstants.thin }, right: { style: reportConstants.thin } };
            });

            DiffTestWorksheet.getRow(7).eachCell((cell: Cell) => {
                cell.border = borderStyle;
            });

            DiffTestWorksheet.getRow(8).eachCell((cell: Cell) => {
                cell.border = borderStyle;
            });

            DiffTestWorksheet.getRow(9).eachCell((cell: Cell) => {
                cell.border = { top: { style: reportConstants.thin }, left: { style: reportConstants.thin }, bottom: { style: reportConstants.double }, right: { style: reportConstants.thin } };
            });

            for (let r = 10; r <= lastRow; r++) {
                for (let c = 1; c < lastCol; c++) {
                    DiffTestWorksheet.getCell(r, c).border = borderStyle;
                }
            }

            for (let r = 4; r <= 5; r++) {
                for (let c = 1; c < lastCol; c++) {
                    DiffTestWorksheet.getCell(r, c).border = {};
                }
            }

            DiffTestWorksheet.getCell(7, 1).border = {
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            DiffTestWorksheet.getColumn(1).width = 15;
            for (let i = 2; i <= lastCol; i++) {
                DiffTestWorksheet.getColumn(i).width = 9;
            }

            this.AddOuterBorder(DiffTestWorksheet, 4, lastRow + 1, 1, lastCol - 1);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateDiffLibTestResidualOilComp(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        try {
            const diffLibResOilCompWorksheet = workbook.addWorksheet(worksheetName);

            const borderStyle: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };

            diffLibResOilCompWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(diffLibResOilCompWorksheet, undefined, {
                orientation: reportConstants.portrait,
                fitToHeight: 0,
                fitToWidth: 0,
                fitToPage: false,
                scale: 75,
                margins: {
                    left: 0.71,
                    right: 0.71,
                    top: 0.25,
                    bottom: 0.55,
                    header: 0.25,
                    footer: 0.55,
                }
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            diffLibResOilCompWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(diffLibResOilCompWorksheet, subHeader, reportConstants.c1, reportConstants.g1);

            diffLibResOilCompWorksheet.mergeCells(reportConstants.a4, reportConstants.e4);
            diffLibResOilCompWorksheet.getRow(4).height = 15;
            const textReportSubtitleCell = diffLibResOilCompWorksheet.getCell(reportConstants.a4);
            textReportSubtitleCell.value = reportConstants.dlResidualOilComposition;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            const tableHeader = [
                [
                    { content: '', colSpan: 2, styles: { halign: reportConstants.center } },
                    { content: reportConstants.residualOil, colSpan: 2, styles: { halign: reportConstants.center } }
                ],
                [
                    { content: reportConstants.component, colSpan: 2, styles: { halign: reportConstants.center } },
                    { content: reportConstants.weightPercent, styles: { halign: reportConstants.center } },
                    { content: reportConstants.molePercent, styles: { halign: reportConstants.center } }
                ]
            ];

            const tableBody = dleExp.dleData.residualOilCompositionResult?.map((val: DLEResidualOilCompositions) => [
                val.component1,
                val.component2,
                this.formatDleResidualOilCompositionValue(val.weightPercent, val.component1),
                this.formatDleResidualOilCompositionValue(val.molePercent, val.component1)
            ]) ?? [];

            const tableStartRow = 5;
            const tableStartColumn = 2;
            const columnWidth = [15, 25, 10, 10];
            for (let colIdx = 0; colIdx < 4; colIdx++) {
                diffLibResOilCompWorksheet.getColumn(tableStartColumn + colIdx).width = columnWidth[colIdx];
            }

            tableHeader.forEach((headerRow, idx) => {
                let colSpan = 0;
                headerRow.forEach((header, colIdx) => {
                    if (header.colSpan && header.colSpan > 1) {
                        diffLibResOilCompWorksheet.mergeCells(tableStartRow + idx, tableStartColumn + colIdx + colSpan, tableStartRow + idx, tableStartColumn + colIdx + colSpan + header.colSpan - 1);
                        colSpan += header.colSpan - 1;
                    }
                    const cell = diffLibResOilCompWorksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx + colSpan);
                    cell.value = header.content;
                    cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                    if (idx > 0 || colIdx > 0) cell.border = borderStyle;
                    diffLibResOilCompWorksheet.getRow(tableStartRow + idx).height = 15;
                });
            });

            tableBody.forEach((row: (string | number)[], rowIdx: number) => {
                row.forEach((cellValue: string | number, colIdx: number) => {
                    const cell = diffLibResOilCompWorksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: (colIdx < 2) };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                    cell.border = rowIdx === 0
                        ? { top: { style: reportConstants.double }, left: borderStyle.left, bottom: borderStyle.bottom, right: borderStyle.right }
                        : borderStyle;
                    diffLibResOilCompWorksheet.getRow(tableStartRow + rowIdx).height = 15;
                });
            });

            const rowNo = tableStartRow + tableHeader.length + tableBody.length;
            this.AddOuterBorder(diffLibResOilCompWorksheet, 3, rowNo, 1, 7);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateDiffLibMaterialBalanceSummary(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        try {
            const materialBalanceSummaryWorksheet = workbook.addWorksheet(worksheetName);

            materialBalanceSummaryWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];
            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(materialBalanceSummaryWorksheet, undefined, {
                orientation: reportConstants.portrait,
                scale: 100
            });

            materialBalanceSummaryWorksheet.getColumn(reportConstants.c).width = 15;

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            materialBalanceSummaryWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(materialBalanceSummaryWorksheet, subHeader, reportConstants.c1, reportConstants.h1);
            materialBalanceSummaryWorksheet.mergeCells(reportConstants.a4, reportConstants.g4);
            const textReportSubtitleCell = materialBalanceSummaryWorksheet.getCell(reportConstants.a4);
            textReportSubtitleCell.value = reportConstants.materialBalanceCalculationSummary;
            textReportSubtitleCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReportSubtitleCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            let rowNo = 6;
            const tableStartColumn = 1;

            const massesReservoirFluid = dleExp.dleData.materialBalanceSummaryResult?.massesReservoirFluid ?? [];
            const massesReservoirFluidData = [
                [reportConstants.dlInitialMass, massesReservoirFluid[0].initialMass, reportConstants.g],
                [reportConstants.dlMaterialBalanceCalculatedMass, massesReservoirFluid[0].mbCalculatedMass, reportConstants.g],
                [reportConstants.dlDifference, massesReservoirFluid[0].diff, reportConstants.percentage]
            ];

            GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.massesOfReservoirFluid, massesReservoirFluidData as string[][], tableStartColumn);
            rowNo += massesReservoirFluidData.length + 2;

            const massesStockTankOil = dleExp.dleData.materialBalanceSummaryResult?.massesStockTankOil ?? [];
            const massesStockTankOilData = [
                [reportConstants.dlMeasuredSTOMass, massesStockTankOil[0].measuredSTOmass, reportConstants.g],
                [reportConstants.dlMaterialBalanceCalculatedSTOMass, massesStockTankOil[0].calculatedSTOmass, reportConstants.g],
                [reportConstants.dlDifference, massesStockTankOil[0].diff, reportConstants.percentage]
            ];

            GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.massesOfStockTankOil, massesStockTankOilData as string[][], tableStartColumn);
            rowNo += massesStockTankOilData.length + 2;

            const molesReservoirFluid = dleExp.dleData.materialBalanceSummaryResult?.molesReservoirFluid ?? [];
            const molesReservoirFluidData = [
                [reportConstants.initialMoles, molesReservoirFluid[0].initialMoles, reportConstants.moles],
                [reportConstants.materialBalanceCalculatedMoles, molesReservoirFluid[0].calculatedMoles, reportConstants.moles],
                [reportConstants.dlDifference, molesReservoirFluid[0].diff, reportConstants.percentage]
            ];

            GenerateSummaryTable(materialBalanceSummaryWorksheet, rowNo, reportConstants.dlMolesOfReservoirFluid, molesReservoirFluidData as string[][], tableStartColumn);
            rowNo += molesReservoirFluidData.length + 2;

            rowNo += 2;
            this.AddOuterBorder(materialBalanceSummaryWorksheet, 3, rowNo, 1, 8);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function GenerateSummaryTable(worksheet: Worksheet, rowNo: number, title: string, tableData: string[][], tableStartColumn: number) {
            worksheet.mergeCells(rowNo, tableStartColumn, rowNo, tableStartColumn + 5);
            const massesReservoirFluidCell = worksheet.getCell(rowNo, tableStartColumn);
            massesReservoirFluidCell.value = title;
            massesReservoirFluidCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: true };
            massesReservoirFluidCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo++;

            const borderStyleTable: Partial<Borders> = {
                top: { style: reportConstants.thin },
                left: { style: reportConstants.thin },
                bottom: { style: reportConstants.thin },
                right: { style: reportConstants.thin }
            };
            tableData.forEach((row: string[], rowIdx: number) => {
                let colSpan = 0;
                row.forEach((cellValue: string, colIdx: number) => {
                    if (colIdx === 0) {
                        worksheet.mergeCells(rowNo + rowIdx, tableStartColumn + colIdx + colSpan, rowNo + rowIdx, tableStartColumn + colIdx + colSpan + 5);
                        colSpan += 5;
                    }
                    const cell = worksheet.getCell(rowNo + rowIdx, tableStartColumn + colIdx + colSpan);
                    const isNumber = !isNaN(Number(cellValue)) && cellValue !== '';
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                    cell.alignment = { horizontal: isNumber ? reportConstants.center : reportConstants.left, vertical: reportConstants.middle };
                    cell.border = borderStyleTable;
                    worksheet.getRow(rowNo + rowIdx).height = 15;
                });
            });
        }
    }

    public AddOuterBorder(
        worksheet: Worksheet,
        startRowNo: number,
        endRowNo: number,
        startColumnNo: number,
        endColumnNo: number
    ) {
        for (let r = startRowNo; r <= endRowNo; r++) {
            for (let c = startColumnNo; c <= endColumnNo; c++) {
                const cell = worksheet.getCell(r, c);
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                cell.border = {
                    top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }

    GenerateTableWithBordersNew(
        worksheet: Worksheet,
        tableHeader: string[][],
        tableBody: (string | number)[][],
        tableStartRow: number,
        tableStartColumn: number,
        borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
    ) {
        tableHeader.forEach((headerRow, idx) => {
            headerRow.forEach((header, colIdx) => {

                const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                cell.border = {
                    top: idx === 0 ? { style: reportConstants.medium } : borderStyle.top,
                    bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === headerRow.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;

                const headerValue = header.split('\n');
                if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 9;
                } else {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 9;
                }
                worksheet.getRow(tableStartRow + idx).height = 80;
            });
        });

        tableBody.forEach((row, rowIdx) => {
            row.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                setNumericOrTextCellValue(cell, cellValue);
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                cell.border = {
                    bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.medium } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === row.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;
            });
        });
    }

    AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
        const textCommentsCell = worksheet.getCell(column + rowNo);
        textCommentsCell.value = value;
        textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
        textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        worksheet.getRow(rowNo).height = 15;
    }
}